/* ==========================================================================
   BeyondHorsepower — interaction engine
   Progressive enhancement: everything here is additive. If this file never
   runs, the theme renders exactly as before. Config comes from window.BH
   (see snippets/bh-layer.liquid).
   ========================================================================== */
(() => {
  const B = window.BH || {};
  if (window.__bhEngine) return;
  window.__bhEngine = true;

  const root = document.documentElement;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const designMode = !!(window.Shopify && window.Shopify.designMode);
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  /* ---------- capability / quality tier ---------- */
  const hasWebGL = () => {
    try {
      const c = document.createElement('canvas');
      return !!(window.WebGL2RenderingContext && c.getContext('webgl2')) || !!c.getContext('webgl');
    } catch (_) { return false; }
  };
  const tier = () => {
    if (B.intensity === 'minimal') return 'minimal';
    const c = navigator.connection || {};
    if (c.saveData || /(^|-)2g$/.test(c.effectiveType || '')) return 'minimal';
    const mem = navigator.deviceMemory || 4;
    const cores = navigator.hardwareConcurrency || 4;
    if (mem <= 2 || cores <= 2) return 'minimal';
    let t = B.intensity === 'full' ? 'full' : 'balanced';
    if (t === 'full' && (mem <= 4 || cores <= 4)) t = 'balanced';
    return t;
  };

  /* ---------- scroll state shared with scenes ---------- */
  const scroll = (window.BH = Object.assign(B, { scroll: { y: scrollY, v: 0 } })).scroll;
  let lastY = scrollY, lastT = performance.now(), ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame((now) => {
      ticking = false;
      const y = scrollY, dt = Math.max(1, now - lastT);
      const v = ((y - lastY) / dt) * 16; // px per ~frame
      scroll.v = scroll.v * 0.6 + v * 0.4;
      scroll.y = y; lastY = y; lastT = now;
      document.body && document.body.classList.toggle('bh-scrolled', y > 8);
      readingDial.update();
    });
  };
  addEventListener('scroll', onScroll, { passive: true });
  setInterval(() => { scroll.v *= 0.8; }, 80); // decay when scrolling stops

  /* ---------- scroll reveal ---------- */
  let revealIO = null;
  const setupReveal = () => {
    if (reduce || designMode || !('IntersectionObserver' in window)) return;
    if (!revealIO) {
      revealIO = new IntersectionObserver((es) => es.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('bh-in'); revealIO.unobserve(e.target); }
      }), { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    }
    const targets = $$('main .resource-list__item:not([data-bh-reveal]), main h2:not([data-bh-reveal]), .featured-blog-posts h2:not([data-bh-reveal])');
    targets.forEach((el, i) => {
      // never hide the first screen's H1 area or anything already inside the hero
      if (el.closest('[data-testid="media-with-content"]')) return;
      el.setAttribute('data-bh-reveal', '');
      el.style.setProperty('--bh-d', `${(i % 5) * 70}ms`);
      revealIO.observe(el);
    });
    root.classList.add('bh-js');
    // safety net: never leave content hidden
    setTimeout(() => $$('[data-bh-reveal]:not(.bh-in)').forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < innerHeight) el.classList.add('bh-in');
    }), 2500);
  };

  /* ---------- card tilt, magnetic buttons, menu underline origin ---------- */
  const setupPointerFX = () => {
    if (!fine || reduce || B.cursor === false) return;
    let tilted = null, raf = 0, ev = null;
    const apply = () => {
      raf = 0;
      if (!ev) return;
      const el = ev.target.closest && ev.target.closest('.resource-list__item');
      if (tilted && tilted !== el) release(tilted);
      if (el) {
        const r = el.getBoundingClientRect();
        const px = clamp((ev.clientX - r.left) / r.width, 0, 1);
        const py = clamp((ev.clientY - r.top) / r.height, 0, 1);
        el.setAttribute('data-bh-tilt', '');
        el.classList.add('bh-tilting');
        el.style.setProperty('--bh-ry', `${((px - 0.5) * 7).toFixed(2)}deg`);
        el.style.setProperty('--bh-rx', `${((0.5 - py) * 6).toFixed(2)}deg`);
        el.style.setProperty('--bh-mx', `${(px * 100).toFixed(1)}%`);
        el.style.setProperty('--bh-my', `${(py * 100).toFixed(1)}%`);
        tilted = el;
      }
    };
    const release = (el) => {
      el.classList.remove('bh-tilting');
      el.style.setProperty('--bh-ry', '0deg');
      el.style.setProperty('--bh-rx', '0deg');
    };
    document.addEventListener('pointermove', (e) => { ev = e; if (!raf) raf = requestAnimationFrame(apply); }, { passive: true });
    document.addEventListener('pointerleave', () => { if (tilted) release(tilted); tilted = null; }, true);

    /* magnetic buttons via the independent `translate` property */
    let mag = null;
    const magSel = '.button, a.button, button.button';
    document.addEventListener('pointermove', (e) => {
      const b = e.target.closest && e.target.closest(magSel);
      if (mag && mag !== b) { mag.style.translate = ''; mag = null; }
      if (!b || b.disabled) return;
      const r = b.getBoundingClientRect();
      const dx = (e.clientX - (r.left + r.width / 2)) / r.width;
      const dy = (e.clientY - (r.top + r.height / 2)) / r.height;
      b.style.translate = `${(dx * 10).toFixed(1)}px ${(dy * 8).toFixed(1)}px`;
      mag = b;
    }, { passive: true });
    document.addEventListener('pointerout', (e) => {
      const b = e.target.closest && e.target.closest(magSel);
      if (b && !b.contains(e.relatedTarget)) b.style.translate = '';
    });

    /* header menu: underline grows from the side the cursor enters */
    const navSel = '#header-component nav a, #header-component .header-menu a, #header-component [class*="menu-list"] a';
    const edge = (e, a) => { const r = a.getBoundingClientRect(); a.style.setProperty('--bh-ux', `${clamp(((e.clientX - r.left) / r.width) * 100, 0, 100).toFixed(0)}%`); };
    document.addEventListener('pointerover', (e) => { const a = e.target.closest && e.target.closest(navSel); if (a) edge(e, a); });
    document.addEventListener('pointerout', (e) => { const a = e.target.closest && e.target.closest(navSel); if (a && !a.contains(e.relatedTarget)) edge(e, a); });
  };

  /* ---------- reading-progress dial (articles) ---------- */
  const readingDial = (() => {
    let el = null, needle = null, label = null, article = null;
    const SWEEP = 240, START = -120;
    const build = () => {
      const ticks = Array.from({ length: 21 }, (_, i) => {
        const a = ((START + (SWEEP * i) / 20) * Math.PI) / 180;
        const major = i % 5 === 0;
        const r0 = 41, r1 = major ? 34 : 37.5;
        const x = (r, k) => (50 + Math.sin(a) * r).toFixed(2);
        const y = (r, k) => (50 - Math.cos(a) * r).toFixed(2);
        return `<line x1="${x(r0)}" y1="${y(r0)}" x2="${x(r1)}" y2="${y(r1)}" stroke="${major ? '#e6e6e2' : '#8b9096'}" stroke-width="${major ? 1.6 : 1}" stroke-linecap="round"/>`;
      }).join('');
      const d = document.createElement('div');
      d.className = 'bh-dial';
      d.setAttribute('aria-hidden', 'true');
      d.innerHTML = `<svg viewBox="0 0 100 100" focusable="false">
        <defs>
          <radialGradient id="bhDialFace" cx="50%" cy="38%" r="70%"><stop offset="0" stop-color="#2a2d31"/><stop offset="1" stop-color="#0d0e10"/></radialGradient>
          <linearGradient id="bhDialRim" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#f3f3ef"/><stop offset="0.5" stop-color="#8f949a"/><stop offset="1" stop-color="#3a3d41"/></linearGradient>
        </defs>
        <circle cx="50" cy="50" r="49" fill="url(#bhDialRim)"/>
        <circle cx="50" cy="50" r="45.5" fill="url(#bhDialFace)"/>
        ${ticks}
        <text class="bh-pct" x="50" y="72" text-anchor="middle" font-size="11" font-weight="600" fill="#e6e6e2" style="font-variant-numeric:tabular-nums;font-family:inherit">0%</text>
        <g class="bh-needle" style="transform:rotate(${START}deg)">
          <path d="M50 50 L48.4 50 L50 15 L51.6 50 Z" fill="var(--bh-accent)"/>
          <circle cx="50" cy="50" r="4.2" fill="#d9dbdc"/><circle cx="50" cy="50" r="1.6" fill="#15171a"/>
        </g>
      </svg>`;
      document.body.appendChild(d);
      return d;
    };
    return {
      init() {
        if (B.readingDial === false || el) return;
        article = $('.blog-post-content');
        if (!article) return;
        el = build();
        needle = $('.bh-needle', el);
        label = $('.bh-pct', el);
        this.update();
      },
      update() {
        if (!el || !article) return;
        const r = article.getBoundingClientRect();
        const total = Math.max(1, r.height - innerHeight * 0.55);
        const p = clamp((-r.top + innerHeight * 0.15) / total, 0, 1);
        needle.style.transform = `rotate(${START + SWEEP * p}deg)`;
        label.textContent = `${Math.round(p * 100)}%`;
        el.classList.toggle('bh-on', r.top < innerHeight * 0.6 && r.bottom > 0);
      }
    };
  })();

  /* ---------- calculator gauge (reads results, never writes to the calculator) ---------- */
  const setupGauge = () => {
    const host = $('#bh-auto-calculators');
    if (!host || $('.bh-gauge')) return;
    const sources = [
      { id: 'bh-mileage-value', label: 'Mileage' },
      { id: 'bh-trip-cost', label: 'Trip cost' },
      { id: 'bh-ev-efficiency', label: 'EV efficiency' }
    ];
    const panel = document.createElement('div');
    panel.className = 'bh-gauge';
    panel.setAttribute('aria-hidden', 'true');
    panel.innerHTML = '<div class="bh-gauge__body"><canvas width="680" height="544"></canvas></div><div class="bh-gauge__label">Live reading</div>';
    host.parentNode.insertBefore(panel, host);
    const body = $('.bh-gauge__body', panel);
    const cv = $('canvas', panel);
    const ctx = cv.getContext('2d');
    const labelEl = $('.bh-gauge__label', panel);
    const accent = getComputedStyle(root).getPropertyValue('--bh-accent').trim() || '#ff5a1f';

    let shown = 0, target = 0, max = 40, rafId = 0, text = '0';
    const nice = (v) => { const p = Math.pow(10, Math.floor(Math.log10(Math.max(v, 1e-6)))); const n = v / p; return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * p * 1.0; };
    const W = cv.width, H = cv.height, cx = W / 2, cy = H * 0.56, R = W * 0.36;
    const a0 = (150 * Math.PI) / 180, a1 = (390 * Math.PI) / 180;

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      // bezel
      let g = ctx.createLinearGradient(0, cy - R, 0, cy + R);
      g.addColorStop(0, '#f2f2ee'); g.addColorStop(0.5, '#8a8f95'); g.addColorStop(1, '#2f3236');
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, R * 1.16, 0, 7); ctx.fill();
      g = ctx.createRadialGradient(cx, cy - R * 0.4, R * 0.1, cx, cy, R * 1.08);
      g.addColorStop(0, '#2b2e32'); g.addColorStop(1, '#0c0d0f');
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, R * 1.08, 0, 7); ctx.fill();
      // track + value arc
      ctx.lineCap = 'round'; ctx.lineWidth = R * 0.09;
      ctx.strokeStyle = 'rgba(255,255,255,0.1)'; ctx.beginPath(); ctx.arc(cx, cy, R * 0.86, a0, a1); ctx.stroke();
      const f = clamp(shown / max, 0, 1);
      ctx.strokeStyle = accent; ctx.beginPath(); ctx.arc(cx, cy, R * 0.86, a0, a0 + (a1 - a0) * f); ctx.stroke();
      // ticks + numerals
      ctx.fillStyle = '#d8dad9'; ctx.strokeStyle = '#9aa0a6'; ctx.font = `${R * 0.12}px sans-serif`; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      for (let i = 0; i <= 40; i++) {
        const a = a0 + ((a1 - a0) * i) / 40, major = i % 5 === 0;
        ctx.lineWidth = major ? 3 : 1.4;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a) * R * (major ? 0.66 : 0.7), cy + Math.sin(a) * R * (major ? 0.66 : 0.7));
        ctx.lineTo(cx + Math.cos(a) * R * 0.76, cy + Math.sin(a) * R * 0.76);
        ctx.stroke();
        if (major && i % 10 === 0) {
          const v = (max * i) / 40;
          ctx.fillText(v >= 100 ? Math.round(v) : +v.toFixed(1), cx + Math.cos(a) * R * 0.52, cy + Math.sin(a) * R * 0.52);
        }
      }
      // needle
      const na = a0 + (a1 - a0) * f;
      ctx.save(); ctx.translate(cx, cy); ctx.rotate(na);
      ctx.fillStyle = accent; ctx.beginPath(); ctx.moveTo(-R * 0.1, -3); ctx.lineTo(R * 0.8, 0); ctx.lineTo(-R * 0.1, 3); ctx.closePath(); ctx.fill();
      ctx.restore();
      ctx.fillStyle = '#d9dbdc'; ctx.beginPath(); ctx.arc(cx, cy, R * 0.07, 0, 7); ctx.fill();
      // digital readout
      ctx.fillStyle = '#f2f2ee'; ctx.font = `600 ${R * 0.24}px sans-serif`; ctx.fillText(text, cx, cy + R * 0.42);
    };
    const tick = () => {
      shown += (target - shown) * 0.09;
      if (Math.abs(target - shown) < max * 0.0008) shown = target;
      draw();
      rafId = shown === target ? 0 : requestAnimationFrame(tick);
    };
    const go = () => { if (!rafId) rafId = requestAnimationFrame(tick); };
    draw();

    const last = {};
    const read = () => {
      sources.forEach((s) => {
        const n = document.getElementById(s.id);
        if (!n) return;
        const raw = (n.textContent || '').replace(/,/g, '');
        const m = raw.match(/-?\d+(\.\d+)?/);
        if (!m) return;
        const v = parseFloat(m[0]);
        if (!isFinite(v) || last[s.id] === raw) return;
        last[s.id] = raw;
        if (v <= 0) return;
        max = nice(v * 1.3);
        target = v;
        text = raw.trim().slice(0, 14);
        labelEl.textContent = s.label;
        go();
      });
    };
    let pending = 0;
    new MutationObserver(() => { if (!pending) pending = requestAnimationFrame(() => { pending = 0; read(); }); })
      .observe(host, { subtree: true, childList: true, characterData: true, attributes: true });
    read();

    if (fine && !reduce) {
      panel.addEventListener('pointermove', (e) => {
        const r = panel.getBoundingClientRect();
        body.style.setProperty('--bh-gy', `${(((e.clientX - r.left) / r.width - 0.5) * 12).toFixed(1)}deg`);
        body.style.setProperty('--bh-gx', `${((0.5 - (e.clientY - r.top) / r.height) * 10).toFixed(1)}deg`);
      });
      panel.addEventListener('pointerleave', () => { body.style.setProperty('--bh-gx', '0deg'); body.style.setProperty('--bh-gy', '0deg'); });
    }
  };

  /* ---------- 3D scenes (lazy) ---------- */
  const handles = new Set();
  let scenesP = null;
  const loadScenes = () => scenesP || (scenesP = import(B.urls.scenes));
  const idle = (fn) => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 2500 }) : setTimeout(fn, 400));
  const onLoaded = (fn) => (document.readyState === 'complete' ? fn() : addEventListener('load', fn, { once: true }));
  const isDarkText = (el) => {
    const m = (getComputedStyle(el).color || '').match(/\d+(\.\d+)?/g);
    if (!m) return false;
    const [r, g, b] = m.map(Number);
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 < 0.55; // dark text => light studio
  };

  const setupHero = () => {
    if (B.hero3d === false || B.template !== 'index' || reduce) return;
    const t = tier();
    if (t === 'minimal' || !hasWebGL()) return;
    const hero = $('[data-testid="media-with-content"]');
    const media = hero && $('.media-block', hero);
    if (!media || $('.bh-stage', media)) return;
    const stage = document.createElement('div');
    stage.className = 'bh-stage';
    stage.setAttribute('aria-hidden', 'true');
    media.appendChild(stage);

    const img = $('img', media);
    const imgReady = () => new Promise((res) => {
      if (!img || img.complete) return res();
      img.addEventListener('load', res, { once: true });
      img.addEventListener('error', res, { once: true });
    });
    const heading = $('h1, .h1', hero) || $('p', hero);
    const dark = heading ? !isDarkText(heading) : true;

    const boot = () => {
      Promise.all([imgReady(), loadScenes()]).then(([, mod]) =>
        mod.mountHero(stage, {
          three: B.urls.three, tier: t, accent: B.accent, dark,
          onFail: () => { h && h.dispose(); stage.remove(); }
        })
      ).then((handle) => {
        if (!handle) return;
        h = handle; handle.stage = stage; handles.add(handle);
        requestAnimationFrame(() => stage.classList.add('bh-ready'));
      }).catch(() => stage.remove()); // any failure => original image stays
    };
    let h = null;
    const io = new IntersectionObserver((es) => {
      if (es[0].isIntersecting) { io.disconnect(); onLoaded(() => idle(boot)); }
    }, { rootMargin: '200px' });
    io.observe(stage);
  };

  const setup404 = () => {
    if (B.template !== '404' || reduce) return;
    if (tier() === 'minimal' || !hasWebGL()) return;
    const h1 = $('main h1');
    if (!h1 || $('.bh-wheel')) return;
    const host = document.createElement('div');
    host.className = 'bh-wheel';
    host.setAttribute('aria-hidden', 'true');
    h1.closest('.section, section, main > *')?.insertAdjacentElement('afterbegin', host);
    const dark = matchMedia('(prefers-color-scheme: dark)').matches && !isDarkText(h1);
    onLoaded(() => idle(() => loadScenes().then((m) => m.mountWheel(host, { three: B.urls.three, accent: B.accent, dark }))
      .then((hd) => { hd && handles.add(hd); })
      .catch(() => host.remove())));
  };

  /* ---------- lifecycle ---------- */
  const init = () => {
    setupReveal();
    setupPointerFX();
    readingDial.init();
    setupGauge();
    setupHero();
    setup404();
    onScroll();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  // Theme editor: re-run idempotent setup and dispose scenes whose DOM was removed
  document.addEventListener('shopify:section:load', () => { setupReveal(); setupHero(); setupGauge(); });
  document.addEventListener('shopify:section:unload', () => {
    handles.forEach((hd) => { if (hd.stage && !hd.stage.isConnected) { hd.dispose(); handles.delete(hd); } });
  });
  addEventListener('pagehide', () => handles.forEach((hd) => hd.dispose()));
})();
